/**
 * A controller for managing dependent select relationships where one select controls
 * the visibility of other form fields based on its value.
 *
 * @example Basic Usage
 * ```html
 * <div data-controller="dependent-select">
 *   <!-- Primary select that controls the dependent fields -->
 *   <select data-action="change->dependent-select#toggle">
 *     <option value="option1">Option 1</option>
 *     <option value="option2">Option 2</option>
 *   </select>
 *
 *   <!-- Dependent field group -->
 *   <div data-dependent-select-target="dependent" data-dependent-select-show-when="option1">
 *     Field only visible for option1
 *   </div>
 * </div>
 * ```
 *
 * Targets:
 * - `dependent`: Elements that should be shown/hidden based on the select value
 *
 * Attributes:
 * - `data-dependent-select-show-when`: The value(s) that should make this target visible
 *   Can be a single value or comma-separated list of values
 */
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["dependent"];

  connect() {
    // Get the initial value from the select
    const select = this.element.querySelector("select");
    if (select) {
      this.toggleFields(select.value);
    }
  }

  toggle(event) {
    this.toggleFields(event.target.value);
  }

  toggleFields(value) {
    this.dependentTargets.forEach((dependent) => {
      const showWhen = dependent.dataset.dependentSelectShowWhen;
      if (!showWhen) return;

      const shouldShow = showWhen.split(",").includes(value);
      dependent.classList.toggle("hidden", !shouldShow);
    });
  }
}
